import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { SUPPORTED_YEARS, type AnnualYearData } from '@/data/annual-calendar/types';
import {
  SEASON_PHASE_STYLE,
  buildMonthGuide,
  type MonthGuideCard,
} from '@/utils/calendarSeasonSummary';

/** Display names the original chips used, pointed at planner coordinates. */
const locationLinks: Record<string, { name: string; lat: number; lon: number }> = {
  'Atacama Desert': { name: 'Atacama Desert, Chile', lat: -24.5, lon: -69.25 },
  Atacama: { name: 'Atacama Desert, Chile', lat: -24.5, lon: -69.25 },
  'Namib Desert': { name: 'Namib Desert, Namibia', lat: -23.3, lon: 15.3 },
  Uluru: { name: 'Uluru, Australia', lat: -25.3444, lon: 131.0369 },
  'Canary Islands': { name: 'Tenerife, Canary Islands', lat: 28.2916, lon: -16.6291 },
  Moab: { name: 'Moab, Utah', lat: 38.5733, lon: -109.5498 },
  'Death Valley': { name: 'Death Valley, California', lat: 36.5323, lon: -116.9325 },
  'Grand Canyon': { name: 'Grand Canyon, Arizona', lat: 36.1069, lon: -112.1129 },
  Dolomites: { name: 'Dolomites, Italy', lat: 46.4102, lon: 11.844 },
  Snowdonia: { name: 'Snowdonia, Wales', lat: 53.0685, lon: -3.8955 },
  'Mauna Kea': { name: 'Mauna Kea, Hawaii', lat: 19.8207, lon: -155.468 },
  Tenerife: { name: 'Tenerife, Canary Islands', lat: 28.2916, lon: -16.6291 },
  Jasper: { name: 'Jasper National Park, Canada', lat: 52.8734, lon: -117.9543 },
  'Cherry Springs': { name: 'Cherry Springs, Pennsylvania', lat: 41.6628, lon: -77.8231 },
  'Aoraki Mackenzie': { name: 'Aoraki Mackenzie, New Zealand', lat: -44.0015, lon: 170.1636 },
  'Torres del Paine': { name: 'Torres del Paine, Chile', lat: -50.9423, lon: -73.4068 },
};

function locationUrl(displayName: string): string {
  const loc = locationLinks[displayName];
  if (!loc) return '/planner';
  return `/planner?name=${encodeURIComponent(loc.name)}&lat=${loc.lat}&lon=${loc.lon}`;
}

const strong = 'text-foreground font-medium';

function LocationChips({ names }: { names: readonly string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {names.map((loc) => (
        <Link
          key={loc}
          to={locationUrl(loc)}
          className="text-xs bg-secondary/80 text-foreground/70 hover:text-gold hover:bg-gold/10 px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors"
        >
          <MapPin className="w-3 h-3" />
          {loc}
        </Link>
      ))}
    </div>
  );
}

function CardShell({ card, children }: { card: MonthGuideCard; children: ReactNode }) {
  const style = SEASON_PHASE_STYLE[card.tone];
  return (
    <div data-month-card={card.id} className={`border-l-4 ${style.border} pl-5 py-1`}>
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-base font-semibold text-foreground">{card.title}</h3>
        {card.badge ? (
          <span className={`text-[11px] font-medium uppercase tracking-wider ${style.badge} px-2 py-0.5 rounded-full`}>
            {card.badge}
          </span>
        ) : null}
      </div>
      {children}
      <LocationChips names={card.chips} />
    </div>
  );
}

function NextYearClose({ year }: { year: number }) {
  const next = year + 1;
  if (SUPPORTED_YEARS.includes(next)) {
    return (
      <>
        start planning with our{' '}
        <Link to={`/milky-way-calendar/${next}`} className="text-gold hover:text-gold-light">
          {next} calendar
        </Link>
        .
      </>
    );
  }
  return <>check our {next} calendar when it&apos;s available.</>;
}

/**
 * The pre-#244 month cards: same borders, pill, type, bold names, and chips.
 * The pill names the locations whose best month falls in the card.
 */
export function AnnualMonthGuide({
  year,
  data,
  heading,
}: {
  year: number;
  data: AnnualYearData;
  heading: string;
}) {
  const cards = buildMonthGuide(data);
  const card = (id: string) => {
    const found = cards.find((item) => item.id === id);
    if (!found) throw new Error(`Missing month card ${id}`);
    return found;
  };
  const jan = card('jan-feb');
  const mar = card('mar-apr');
  const may = card('may-jul');
  const aug = card('aug-sep');
  const oct = card('oct-dec');

  return (
    <section className="section-alt py-12 sm:py-16">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-[-0.02em] mb-3">
          {heading}
        </h2>
        <p className="text-sm text-muted-foreground mb-8">
          Here&apos;s what the galactic core does in each stretch of the year, and where this calendar&apos;s Saturdays line up best.
        </p>

        <div className="max-w-none space-y-6 text-muted-foreground leading-relaxed">
          <CardShell card={jan}>
            <p className="mb-3">
              The galactic core spends most of these nights below the horizon. From the Southern Hemisphere it
              starts climbing back into the pre-dawn sky, so the <strong className={strong}>Atacama Desert</strong>,{' '}
              <strong className={strong}>Namib Desert</strong>, and <strong className={strong}>Uluru</strong> get the
              year&apos;s first core shots, low in the southeast before twilight. In the north, most locations above
              40°N won&apos;t see it in full darkness, though lower-latitude spots like the American Southwest and the{' '}
              <strong className={strong}>Canary Islands</strong> can catch a short window just before dawn by late
              February. {jan.gridSentence} I use these weeks to scout locations and plan the trips later in the year.
            </p>
          </CardShell>

          <CardShell card={mar}>
            <p className="mb-3">
              By March the core clears the southeastern horizon a few hours before dawn from most mid-northern
              latitudes, and it rises a little earlier each week. <strong className={strong}>Moab</strong>,{' '}
              <strong className={strong}>Death Valley</strong>, and the <strong className={strong}>Grand Canyon</strong>{' '}
              get that pre-dawn window first. By April it reaches European locations like the{' '}
              <strong className={strong}>Dolomites</strong> and <strong className={strong}>Snowdonia</strong>, where
              the window is shorter and the core sits lower. {mar.gridSentence} The core lies along the southeastern
              horizon as a low, flat arch, which suits panoramas with a strong foreground. I plan these shoots around
              the new moon, since the window is short to begin with.
            </p>
          </CardShell>

          <CardShell card={may}>
            <p className="mb-3">
              These are the months when the core is up for most of the dark hours. At mid-northern latitudes
              (35°–45°N), a dark night gives you roughly four to six hours with the core above the horizon, and it
              goes from a low arch in May to a near-vertical band by July. Lower-latitude locations like{' '}
              <strong className={strong}>Mauna Kea</strong> and <strong className={strong}>Tenerife</strong> get the
              longest windows. {may.gridSentence} In the Southern Hemisphere the core passes nearly overhead from the{' '}
              <strong className={strong}>Atacama</strong> and the <strong className={strong}>Namib Desert</strong>,
              and that&apos;s where it looks brightest and most detailed.
            </p>
          </CardShell>

          <CardShell card={aug}>
            <p className="mb-3">
              The core is already up when it gets dark, low in the south and drifting toward the southwest through
              the evening, and it sets a little earlier each week. A lot of photographers like this stretch because
              you can shoot right after astronomical twilight ends without staying up until 2 AM. Northern locations
              like <strong className={strong}>Jasper</strong> and <strong className={strong}>Cherry Springs</strong>{' '}
              get longer, darker nights back after the short summer ones. {aug.gridSentence} Down south,{' '}
              <strong className={strong}>Aoraki Mackenzie</strong> and{' '}
              <strong className={strong}>Torres del Paine</strong> have the core high in the evening sky while their
              nights shorten toward spring.
            </p>
          </CardShell>

          <CardShell card={oct}>
            <p className="mb-3">
              By October the core is low in the southwest at dusk and sets soon after full darkness, and by
              December the Northern Hemisphere has almost nothing left to work with. The Southern Hemisphere
              holds on a little longer. From the <strong className={strong}>Atacama</strong> and{' '}
              <strong className={strong}>Aoraki Mackenzie</strong> the core is still in the western sky after dark
              into November, then it drops out there too. {oct.gridSentence} This is a good time to plan next
              year&apos;s shoots, book travel to dark-sky destinations, and <NextYearClose year={year} />
            </p>
          </CardShell>
        </div>
      </div>
    </section>
  );
}
